import { usePagination } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { api } from 'src/common/utils/api';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { BaseRecord } from '../types';
import { TableData } from './useTable';

export const useServerPaginatedTable = <T extends BaseRecord>(
  resource: string,
  pageSize: number,
  filters?: Record<string, unknown>,
): TableData<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = Number(searchParams.get('page')) || 1;

  const setCurrent = (page: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
  };

  const resourceSegments = resource.split('/');

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [...resourceSegments, { page: current, size: pageSize, ...filters }],
    queryFn: () => api.getPaginated<T>(resource, { page: current, size: pageSize, ...filters }),
  });

  const processedData = data?.items ?? [];
  const processedTotalRecords = data?.total ?? 0;
  const pageCount = Math.ceil(processedTotalRecords / pageSize);

  useEffect(() => {
    if (isError && error) {
      notifyAndLogError(
        'Failed to load data',
        'An error occurred while loading table data. Please try again later.',
        error,
        { resource, current, pageSize },
      );
    }
  }, [isError, error, resource, current, pageSize]);

  const pagination = usePagination({
    total: pageCount,
    page: current,
    onChange: (newPage: number) => {
      try {
        setCurrent(newPage);
      } catch (error) {
        notifyAndLogError(
          'Navigation failed',
          'Failed to navigate to the requested page. Please try again.',
          error,
          { resource, current, newPage },
        );
      }
    },
  });

  return {
    data: processedData,
    isLoading,
    isFetching,
    isError,
    error,
    pagination,
    totalRecords: processedTotalRecords,
    refetch: () => {
      try {
        return refetch();
      } catch (error) {
        notifyAndLogError(
          'Refresh failed',
          'Failed to refresh table data. Please try again.',
          error,
          { resource },
        );
        throw error;
      }
    },
    sortStatus: undefined,
    onSortStatusChange: undefined,
  };
};
